import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProgressiveImg from './ProgressiveImg';
import { toFullMediaUrl, toFullMediaThumb, toVideoPosterUrl } from '../../utils/mediaUrl';
import { classifyTouchAxis, TOUCH_GESTURE_THRESHOLD } from '../../utils/touchGesture';
import UnifiedVideo from './UnifiedVideo';
import './MediaCarousel.css';

const isLocalMediaUrl = (value) => /^(?:blob:|data:)/i.test(String(value || '').trim());

function decodeImageUrl(url) {
  if (!url || typeof window === 'undefined' || typeof window.Image !== 'function') {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    const image = new window.Image();
    image.fetchPriority = 'low';
    image.decoding = 'async';
    let settled = false;
    const finish = async (loaded) => {
      if (settled) return;
      settled = true;
      if (!loaded) {
        resolve(false);
        return;
      }
      if (typeof image.decode === 'function') {
        try {
          await image.decode();
        } catch (_) {
          // A successful load is still usable on browsers with incomplete
          // Image.decode implementations.
        }
      }
      resolve(true);
    };
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = url;
    if (image.complete && image.naturalWidth > 0) finish(true);
  });
}

/**
 * MediaCarousel — a vertical 3:4 photo / video carousel with:
 *   - swipe gestures (left / right to change slide)
 *   - tap to pause/resume videos and toggle their audio
 *   - click photo to open lightbox (via onPhotoClick)
 *   - dot indicators (only when >1 media item)
 *   - counter (only when >1 media item)
 *
 * Videos: only the active one plays while this carousel is meaningfully
 * visible in the viewport. Other carousels stay paused until the user reaches
 * them.
 */
const MediaCarousel = ({ media = [], onPhotoClick, onVideoDoubleTap, onVideoProgress, onActiveMediaChange, soundButtonClassName = '', aspectRatio = '3 / 4', eager = false, showMobileVideoControls = false, autoplayWithSound = false, preferOriginalActive = false, activeThumbWidth = 1024 }) => {
  const SWIPE_THRESHOLD = 38;
  const total = media.length;
  const [index, setIndex] = useState(0);
  const [mediaShapes, setMediaShapes] = useState({});
  // Prepare a video before its card reaches the screen. UnifiedVideo owns
  // autoplay policy and the global one-player rule.
  const [isNearViewport, setIsNearViewport] = useState(eager);
  const [isViewportVisible, setIsViewportVisible] = useState(eager);
  const startX = useRef(0);
  const startY = useRef(0);
  const deltaX = useRef(0);
  const deltaY = useRef(0);
  const gestureAxis = useRef(null);
  const ignoreClick = useRef(false);
  const swiping = useRef(false);
  const videoRef = useRef(null);
  const viewportRef = useRef(null);
  const carouselRef = useRef(null);
  const navigationRequestRef = useRef(0);
  const decodedPreviewPromisesRef = useRef(new Map());
  const registerMediaShape = useCallback((mediaKey, element) => {
    const width = element?.naturalWidth || element?.videoWidth;
    const height = element?.naturalHeight || element?.videoHeight;
    if (!width || !height) return;
    const shape = height > width ? 'portrait' : 'landscape';
    setMediaShapes((previous) => (
      previous[mediaKey] === shape
        ? previous
        : { ...previous, [mediaKey]: shape }
    ));
  }, []);

  const stopAllVideos = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.querySelectorAll('video').forEach((video) => {
      video.pause();
      // Resetting the playhead cuts the audio immediately when a swipe moves
      // from a video to a photograph or another video.
      try {
        video.currentTime = 0;
      } catch (_) {
        // A video without metadata yet may reject currentTime.
      }
    });
  }, []);

  // Do not warm galleries that are far below the fold. The feed can mount
  // many cards at once; this keeps their neighbouring images out of the
  // network queue until the card is actually close to the viewport.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof window === 'undefined') return undefined;
    if (typeof window.IntersectionObserver !== 'function') {
      setIsNearViewport(true);
      setIsViewportVisible(true);
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        const near = Boolean(entry?.isIntersecting);
        setIsNearViewport(near);
        setIsViewportVisible(near && entry.intersectionRatio >= 0.25);
      },
      // Buffer cards before they reach the screen. The visibility threshold
      // controls efficient preloading; the active video starts by itself.
      { rootMargin: '480px 0px', threshold: [0, 0.25] },
    );
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [eager]);

  const ensureMediaReady = useCallback((item) => {
    if (!item?.src) return Promise.resolve(false);
    const localPath = isLocalMediaUrl(item.src);
    const posterPath = item.poster || toVideoPosterUrl(item.src);
    const previewPath = item.type === 'video'
      ? posterPath
      : (localPath ? item.src : toFullMediaThumb(item.src, { width: 640 }));
    const previewUrl = previewPath
      ? (isLocalMediaUrl(previewPath) ? previewPath : toFullMediaUrl(previewPath) || previewPath)
      : null;
    const originalUrl = localPath ? item.src : toFullMediaUrl(item.src) || item.src;
    const cacheKey = previewUrl || originalUrl;
    if (!cacheKey) return Promise.resolve(false);

    const cached = decodedPreviewPromisesRef.current.get(cacheKey);
    if (cached) return cached;

    const sources = item.type === 'video'
      ? [previewUrl]
      : [...new Set([previewUrl, originalUrl].filter(Boolean))];
    const promise = (async () => {
      for (const source of sources) {
        if (await decodeImageUrl(source)) return true;
      }
      return false;
    })();
    decodedPreviewPromisesRef.current.set(cacheKey, promise);
    promise.then((ready) => {
      if (!ready) decodedPreviewPromisesRef.current.delete(cacheKey);
    });
    return promise;
  }, []);

  const goto = useCallback((i) => {
    const next = (i + total) % Math.max(total, 1);
    const target = media[next];
    if (!target || total < 2) return;
    const request = ++navigationRequestRef.current;

    // Keep the current media visible while the target preview loads and
    // decodes. Then swap directly: no fade, slide animation or blank canvas.
    ensureMediaReady(target).then((ready) => {
      if (!ready || request !== navigationRequestRef.current) return;
      stopAllVideos();
      setIndex(next);
      onActiveMediaChange?.(target.type === 'video', next);
      onVideoProgress?.(0, 0, null);
    });
  }, [ensureMediaReady, media, onActiveMediaChange, onVideoProgress, stopAllVideos, total]);

  useEffect(() => () => {
    navigationRequestRef.current += 1;
  }, []);

  // Mobile feed metadata (author/title) is rendered as an overlay above the
  // carousel. Its links need to remain tappable, but a horizontal gesture
  // that starts on those links must still advance the underlying gallery.
  // Post dispatches this small internal event when that happens.
  useEffect(() => {
    const node = carouselRef.current;
    if (!node) return undefined;
    const handleExternalSwipe = (event) => {
      const deltaX = Number(event?.detail?.deltaX);
      if (!Number.isFinite(deltaX) || Math.abs(deltaX) <= SWIPE_THRESHOLD || total <= 1) return;
      goto(deltaX < 0 ? index + 1 : index - 1);
    };
    node.addEventListener('gm-carousel-swipe', handleExternalSwipe);
    return () => node.removeEventListener('gm-carousel-swipe', handleExternalSwipe);
  }, [goto, index, total]);

  // Let the feed place its progress bar only for the currently visible media
  // item. A trip can contain both photos and videos, so checking whether the
  // gallery has any video at all would leave a stale timeline over photos.
  useEffect(() => {
    onActiveMediaChange?.(media[index]?.type === 'video', index);
  }, [index, media, onActiveMediaChange]);

  // Warm only the two items reachable on the next swipe. The active item
  // stays visible until the decoded preview is ready, so there is no loading
  // canvas to flash between slides.
  useEffect(() => {
    if (!isViewportVisible || total <= 1 || typeof window === 'undefined') return undefined;

    const indices = Array.from(new Set([
      (index + 1) % total,
      (index - 1 + total) % total,
    ]));
    indices.forEach((i) => ensureMediaReady(media[i]));
    return undefined;
  }, [ensureMediaReady, index, isViewportVisible, media, total]);

  const onPointerDown = (e) => {
    const point = e.touches ? e.touches[0] : e;
    startX.current = point.clientX;
    startY.current = point.clientY;
    deltaX.current = 0;
    deltaY.current = 0;
    gestureAxis.current = null;
    ignoreClick.current = false;
    swiping.current = true;
  };

  const onPointerMove = (e) => {
    if (!swiping.current) return;
    const point = e.touches ? e.touches[0] : e;
    deltaX.current = point.clientX - startX.current;
    deltaY.current = point.clientY - startY.current;

    if (!gestureAxis.current) {
      if (Math.hypot(deltaX.current, deltaY.current) < TOUCH_GESTURE_THRESHOLD) return;
      ignoreClick.current = true;
      gestureAxis.current = classifyTouchAxis(deltaX.current, deltaY.current);
      if (!gestureAxis.current) return;
    }

    // Keep horizontal mouse drags inside the carousel. On touch devices,
    // touch-action and the feed's non-passive native guard handle gesture
    // ownership; React's touchmove listener is passive, so preventDefault()
    // there is ignored by the browser and only produces a console warning.
    if (gestureAxis.current === 'horizontal' && e.cancelable && e.type !== 'touchmove') {
      e.preventDefault();
    }
    // Keep the current media stationary. The gallery changes only after
    // release and after the next decoded preview is ready.
  };

  const onPointerUp = (e) => {
    if (!swiping.current) return;

    // Some mobile browsers dispatch the synthetic click after the final
    // touch event without delivering enough `touchmove` events for the
    // threshold above. Read the final touch point as a second guard so a
    // vertical page scroll can never be interpreted as a video tap.
    const point = e?.changedTouches?.[0] || e?.touches?.[0] || e;
    if (point && Number.isFinite(point.clientX) && Number.isFinite(point.clientY)) {
      deltaX.current = point.clientX - startX.current;
      deltaY.current = point.clientY - startY.current;
      if (!gestureAxis.current
        && Math.hypot(deltaX.current, deltaY.current) >= TOUCH_GESTURE_THRESHOLD) {
        gestureAxis.current = classifyTouchAxis(deltaX.current, deltaY.current);
        ignoreClick.current = true;
      }
    }

    swiping.current = false;
    const dx = deltaX.current;
    if (gestureAxis.current === 'horizontal' && Math.abs(dx) > SWIPE_THRESHOLD && total > 1) {
      if (dx < 0) goto(index + 1);
      else goto(index - 1);
    }
  };

  const onPointerCancel = () => {
    swiping.current = false;
    gestureAxis.current = null;
    // A cancelled touch is not an intentional tap. Ignore any click that a
    // browser may synthesize after the cancellation.
    ignoreClick.current = true;
  };

  const onClick = (e) => {
    if (ignoreClick.current) {
      ignoreClick.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const item = media[index];
    if (!item) return;
    if (item.type === 'video') {
      return;
    }
    onPhotoClick?.(item.src, index);
  };

  if (total === 0) {
    return (
      <div className="gm-carousel">
        <div className="gm-carousel__viewport" style={{ aspectRatio }}>
          <div className="gm-carousel__placeholder" />
        </div>
      </div>
    );
  }

  const isAdjacent = (candidateIndex) => {
    if (total <= 1) return candidateIndex === index;
    return candidateIndex === index
      || candidateIndex === (index + 1) % total
      || candidateIndex === (index - 1 + total) % total;
  };

  return (
      <div
      ref={carouselRef}
      className="gm-carousel"
      onTouchStart={onPointerDown}
      onTouchMove={onPointerMove}
      onTouchEnd={onPointerUp}
      onTouchCancel={onPointerCancel}
      onMouseDown={onPointerDown}
      onMouseMove={onPointerMove}
      onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
      >
      <div ref={viewportRef} className="gm-carousel__viewport" style={{ aspectRatio }}>
        <div
          className="gm-carousel__track"
          style={{
            transform: `translate3d(${-index * 100}%, 0, 0)`,
          }}
        >
        {media.map((m, i) => (
          <div
            className={`gm-carousel__slide ${mediaShapes[m.src] === 'portrait' ? 'gm-carousel__slide--portrait' : ''}`}
            key={`${i}-${m.src}`}
            onClick={onClick}
          >
            {m.type === 'video' ? (
              <UnifiedVideo
                ref={i === index ? videoRef : undefined}
                containerClassName="gm-carousel__video-container"
                className="gm-carousel__video"
                src={m.src}
                // Attach only the active and next video. UnifiedVideo still
                // owns playback, autoplay fallback and the global mutex.
                load={(
                  (i === index || (total > 1 && i === (index + 1) % total))
                  && isNearViewport
                )}
                // Keep the active video mounted/active while the user pauses
                // it. Toggling `active` off here races the manual play()
                // promise on mobile and can leave the poster covering resumed
                // frames until the video reaches its end.
                active={i === index}
                visible={i === index && isViewportVisible}
                autoplayWithSound={autoplayWithSound}
                resetOnActivate
                preservePosterUntilFrame
                poster={m.poster || toVideoPosterUrl(m.src) || undefined}
                loop
                showSoundButton={i === index}
                soundButtonClassName={`${soundButtonClassName} gm-video-sound-button--carousel`.trim()}
                showMobileControls={showMobileVideoControls && i === index}
                // The feed owns the visible timeline under the chips. The
                // player still receives mobile controls for the edge-hold 2x
                // gesture, but does not render a second progress bar.
                showMobileProgress={false}
                onProgress={(current, duration) => {
                  if (i !== index) return;
                  onVideoProgress?.(
                    current,
                    duration,
                    (time) => videoRef.current?.seek?.(time),
                  );
                }}
                onDoubleTap={i === index ? (event) => onVideoDoubleTap?.(m.src, i, event) : undefined}
                // Keep metadata warm for the next slide, but reserve the
                // media connection for the active video. `auto` on adjacent
                // videos made iOS download multiple large files at once and
                // left the visible slide on a black frame for longer.
                preload={i === index && isNearViewport ? 'auto' : 'metadata'}
                fetchPriority={i === index && isViewportVisible ? 'high' : 'low'}
                onLoadedMetadata={(e) => registerMediaShape(m.src, e.currentTarget)}
                // Keep the poster/background visible if an old upload cannot
                // be decoded. Hiding the element left a large black tile and
                // made a slow/error response look like a broken carousel.
                onError={(e) => { e.currentTarget.pause(); }}
              />
            ) : isAdjacent(i) ? (
              <ProgressiveImg
                src={m.src}
                alt=""
                // Keep the current, previous and next images mounted. The
                // previous implementation replaced the outgoing image with
                // a dark placeholder before the CSS transform ended, which
                // caused the visible dark flash on every swipe.
                eager={i === index && (eager || isViewportVisible)}
                // Adjacent slides stay lightweight. The active slide retains
                // its established sharp/original source, with the decoded
                // preview underneath until that source is ready.
                thumbWidth={i === index ? activeThumbWidth : 640}
                retainedSrc={isLocalMediaUrl(m.src)
                  ? m.src
                  : toFullMediaThumb(m.src, { width: 640 })}
                skipSrcSet={i === index}
                preferOriginal={preferOriginalActive && i === index}
                sizes="(max-width: 768px) 100vw, 640px"
                keepPreviousOnSourceChange
                onLoad={(e) => registerMediaShape(m.src, e.currentTarget)}
              />
            ) : (
              <div className="gm-carousel__placeholder" aria-hidden="true" />
            )}
            </div>
          ))}
        </div>

        {/* Arrow navigation (desktop) */}
        {total > 1 && (
          <>
            <button
              type="button"
              className="gm-carousel__arrow gm-carousel__arrow--prev"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goto(index - 1); }}
              aria-label="Foto anterior"
            >
              <ChevronLeft size={22} strokeWidth={2.2} />
            </button>
            <button
              type="button"
              className="gm-carousel__arrow gm-carousel__arrow--next"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goto(index + 1); }}
              aria-label="Próxima foto"
            >
              <ChevronRight size={22} strokeWidth={2.2} />
            </button>
          </>
        )}

      </div>

      {/* Dots */}
      {total > 1 && (
        <div className="gm-carousel__dots" aria-hidden="true">
          {media.map((_, i) => (
            <span
              key={i}
              className={`gm-carousel__dot ${i === index ? 'gm-carousel__dot--active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

MediaCarousel.propTypes = {
  media: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['photo', 'video']),
    poster: PropTypes.string,
  })),
  onPhotoClick: PropTypes.func,
  onVideoDoubleTap: PropTypes.func,
  onVideoProgress: PropTypes.func,
  onActiveMediaChange: PropTypes.func,
  soundButtonClassName: PropTypes.string,
  aspectRatio: PropTypes.string,
  eager: PropTypes.bool,
  showMobileVideoControls: PropTypes.bool,
  autoplayWithSound: PropTypes.bool,
  preferOriginalActive: PropTypes.bool,
  activeThumbWidth: PropTypes.oneOf([320, 640, 1024]),
};

export default MediaCarousel;
